// Simple File Preview Dialog for Documents
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { FileText, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const FilePreviewDialog = ({ open, onClose, file }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mimeType, setMimeType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cleanup function to revoke blob URLs
  const cleanupPreviewUrl = useCallback(() => {
    if (previewUrl) {
      try {
        window.URL.revokeObjectURL(previewUrl);
      } catch (err) {
        console.error('Error revoking URL:', err);
      }
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  // Load preview when dialog opens and file is available
  useEffect(() => {
    let isMounted = true;
    let abortController = new AbortController();

    const loadPreview = async () => {
      try {
        // Reset state
        cleanupPreviewUrl();
        setError(null);

        // Guard clauses with extensive validation
        if (!open) {
          setLoading(false);
          return;
        }

        if (!file) {
          console.error('FilePreviewDialog: file prop is null/undefined');
          setLoading(false);
          return;
        }

        if (!file.file_url) {
          console.error('FilePreviewDialog: file.file_url is missing', file);
          setError('File URL is missing');
          setLoading(false);
          return;
        }
        
        setLoading(true);
        
        // Strip leading slash to ensure axios uses the configured baseURL
        let fileUrl = file.file_url;
        if (typeof fileUrl === 'string' && fileUrl.startsWith('/')) {
          fileUrl = fileUrl.substring(1);
        }
        
        // Use fetch directly to avoid axios interceptor issues with blob 404s
        const token = localStorage.getItem('token');
        const baseUrl = window.location.origin || '';
        const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${baseUrl}/api/${fileUrl}`;
        const response = await fetch(fullUrl, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        });
        
        if (!response.ok) {
          throw { response: { status: response.status }, message: `HTTP ${response.status}` };
        }
        
        if (isMounted && !abortController.signal.aborted) {
          const blob = await response.blob();
          if (blob && blob instanceof Blob) {
            const url = window.URL.createObjectURL(blob);
            setPreviewUrl(url);
            setMimeType(blob.type);
          } else {
            throw new Error('Invalid response format');
          }
        }
      } catch (err) {
        if (isMounted && !abortController.signal.aborted) {
          console.error('FilePreviewDialog: Preview error:', err);
          const errorMsg = err.response?.status === 404 
            ? 'File not found' 
            : err.message || 'Failed to load preview';
          setError(errorMsg);
          toast.error(errorMsg);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Wrap loadPreview call in try-catch
    try {
      loadPreview();
    } catch (err) {
      console.error('FilePreviewDialog: Error calling loadPreview:', err);
    }

    // Cleanup function
    return () => {
      isMounted = false;
      try {
        abortController.abort();
      } catch (err) {
        console.error('FilePreviewDialog: Error aborting controller:', err);
      }
    };
  }, [file?.file_url, open]);
  
  // Cleanup blob URL when component unmounts or dialog closes
  useEffect(() => {
    if (!open) {
      cleanupPreviewUrl();
      setMimeType(null);
    }
    return cleanupPreviewUrl;
  }, [open, cleanupPreviewUrl]);

  const handleDownload = async () => {
    try {
      if (!file?.file_url) {
        toast.error('No file available');
        return;
      }
      
      // Strip leading slash to ensure axios uses the configured baseURL
      let fileUrl = file.file_url;
      if (typeof fileUrl === 'string' && fileUrl.startsWith('/')) {
        fileUrl = fileUrl.substring(1);
      }
      
      const response = await api.get(fileUrl, { responseType: 'blob' });
      const blob = response.data;
      if (!blob || !(blob instanceof Blob)) {
        throw new Error('Invalid file data');
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.file_name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded successfully');
    } catch (err) {
      console.error('FilePreviewDialog: Download error:', err);
      toast.error('Download failed: ' + (err.message || 'Unknown error'));
    }
  };

  // Safely determine file type — check filename extension first, then fall back to blob MIME type
  const fileName = file?.file_name || file?.deliverable_type_label || '';
  const isImage = !!(fileName.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i) || mimeType?.startsWith('image/'));
  // Consider it a PDF if filename ends in .pdf, OR mime is pdf, OR mime is octet-stream with no other match
  const isPDF = !!(fileName.match(/\.pdf$/i) || mimeType === 'application/pdf' || (mimeType === 'application/octet-stream' && !isImage));

  // Add safety check for dialog rendering
  if (!file) {
    console.error('FilePreviewDialog: Rendering with null file');
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      try {
        if (!isOpen) {
          onClose();
        }
      } catch (err) {
        console.error('FilePreviewDialog: Error in onOpenChange:', err);
      }
    }}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle>
            {file?.nickname || file?.deliverable_type_label || 'Document Preview'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 p-4 bg-muted/30 min-h-0 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Loading preview...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-3 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button variant="outline" onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-2" /> Try Download Instead
                </Button>
              </div>
            </div>
          ) : previewUrl ? (
            <>
              {isImage ? (
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="max-w-full object-contain mx-auto block"
                  onError={() => {
                    setError('Failed to load image');
                    cleanupPreviewUrl();
                  }}
                />
              ) : isPDF ? (
                <iframe
                  src={previewUrl}
                  title="PDF Preview"
                  className="w-full border-0 rounded bg-white"
                  style={{ height: 'calc(90vh - 140px)' }}
                  onError={() => setError('Unable to display PDF')}
                >
                  <div className="text-center p-8">
                    <p className="text-muted-foreground mb-4">Unable to display PDF in browser</p>
                    <Button variant="outline" onClick={handleDownload}>
                      <Download className="w-4 h-4 mr-2" /> Download PDF
                    </Button>
                  </div>
                </iframe>
              ) : (
                <div className="text-center">
                  <FileText className="w-16 h-16 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-muted-foreground mb-2">Preview not available for this file type</p>
                  {file?.file_name && (
                    <p className="text-xs text-muted-foreground mb-4">{file.file_name}</p>
                  )}
                  <Button variant="outline" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-2" /> Download File
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center">
              <FileText className="w-16 h-16 mx-auto mb-3 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">No preview available</p>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {previewUrl && !error && (
            <Button onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewDialog;
